import { getCurrentStudentLanguage } from '../store/selectors/sharedSelectors'

import { LANGUAGE_TO_TRANSLATION_LANGUAGE, useCanTranslateToLanguage } from './stringTranslator'

import { AVAILABLE_LANGUAGES } from '@mission.io/mission-toolkit/constants'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

const supported = typeof window !== 'undefined' && !!window.speechSynthesis

/**
 * A react hook for the browser's SpeechSynthesis API
 * Copied from https://github.com/MikeyParton/react-speech-kit and modified to use onboundary event
 * @param {{
 *		onEnd: Function, Callback called when SpeechSynthesis finishes reading the text or is cancelled,
 *		onBoundary: Function, Callback fired when the spoken utterance reaches a word or sentence boundary,
 *  }} props
 * @returns {{
 *   loadingInfo: boolean, true if loading if text to speech is supported in the mounted context, false if everything has been loaded
 *	 supported: boolean, will be true if the browser supports SpeechSynthesis,
 *	 speak: Function, call to make the browser read some text,
 *	 speaking: boolean, will be true when SpeechSynthesis is actively speaking,
 *	 cancel: Function, call to make SpeechSynthesis stop reading,
 *	 voices: Array, an array of available voices which can be passed to the speak function,
 *	}}
 */
function useSpeechSynthesis(
	props: Object = {}
): {
	loadingInfo: boolean,
	supported: boolean,
	speak: (args: {
		text: string,
		voice: SpeechSynthesisVoice,
		rate?: number,
		pitch?: number,
		volume?: number,
	}) => void,
	speaking: boolean,
	cancel: () => void,
	voices: Array<SpeechSynthesisVoice>,
} {
	const studentLanguage = useSelector(getCurrentStudentLanguage)
	const supportedLanguage = useCanTranslateToLanguage(
		'en',
		LANGUAGE_TO_TRANSLATION_LANGUAGE[studentLanguage ?? AVAILABLE_LANGUAGES.ENGLISH] ?? 'en'
	)
	const { onEnd = () => {} } = props
	const { onBoundary = (event: any) => {} } = props
	const [voices, setVoices] = useState([])
	const [speaking, setSpeaking] = useState(false)

	const handleEnd = () => {
		setSpeaking(false)
		onEnd()
	}

	useEffect(() => {
		if (!supported) {
			return
		}

		// Firefox seems to have voices upfront and never calls the
		// voiceschanged event
		let voiceOptions = window.speechSynthesis.getVoices()
		if (voiceOptions.length > 0) {
			setVoices(voiceOptions)
			return
		}

		window.speechSynthesis.onvoiceschanged = event => {
			voiceOptions = event.target.getVoices()
			setVoices(voiceOptions)
		}
	}, [])

	const isSpeakingRef = useRef(speaking)
	isSpeakingRef.current = speaking
	const cancel = useCallback(() => {
		if (!supported) return
		if (isSpeakingRef.current) {
			setSpeaking(false)
			window.speechSynthesis.removeEventListener(
				CUSTOM_MISSION_IO_CANCEL_SPEECH_SYNTHESIS_EVENT_TYPE,
				cancel
			)
			cancelCurrentSpeechSynthesis()
		}
	}, [])

	const speak = (args = {}) => {
		const { voice = null, text = '', rate = 1, pitch = 1, volume = 1 } = args
		if (!supported) {
			return
		}
		setSpeaking(true)
		// Firefox won't repeat an utterance that has been
		// spoken, so we need to create a new instance each time
		const utterance = new window.SpeechSynthesisUtterance()
		utterance.text = text
		utterance.voice = voice
		utterance.onend = handleEnd
		utterance.onboundary = onBoundary
		utterance.rate = rate
		utterance.pitch = pitch
		utterance.volume = volume
		cancelCurrentSpeechSynthesis() // stop previous voice overs to start this one
		window.speechSynthesis.speak(utterance)
		window.speechSynthesis.addEventListener(
			CUSTOM_MISSION_IO_CANCEL_SPEECH_SYNTHESIS_EVENT_TYPE,
			cancel,
			{ once: true }
		)
	}

	useEffect(
		() => () => {
			cancel()
		},
		[cancel]
	)

	return {
		loadingInfo: !supported || supportedLanguage.loading,
		supported: supported && !supportedLanguage.loading && supportedLanguage.translationSupported,
		speak,
		speaking,
		cancel,
		voices,
	}
}

const CUSTOM_MISSION_IO_CANCEL_SPEECH_SYNTHESIS_EVENT_TYPE =
	'CUSTOM_MISSION_IO_CANCEL_SPEECH_SYNTHESIS_EVENT_TYPE' // NOTE: this event type is dispatched from the window.speechSynthesis object when the speechSynthesis is canceled.

/**
 * cancelCurrentSpeechSynthesis - cancels the current speech synthesis utterance as well as dispatch the custom cancel event to inform other components of the cancellation.
 */
export function cancelCurrentSpeechSynthesis() {
	if (!supported) {
		return
	}
	window.speechSynthesis.cancel()
	window.speechSynthesis.dispatchEvent(
		new Event(CUSTOM_MISSION_IO_CANCEL_SPEECH_SYNTHESIS_EVENT_TYPE)
	)
}

export default useSpeechSynthesis
